<!-- eslint-disable vue/no-v-html -->
<template>
    <component :is="passedObject ? 'div' : 'card'" :title="`${searchTypeLabel} History`">
        <form>
            <b-row align-v="baseline">
                <b-col cols="2">
                    <fg-input label="Type">
                        <v-select id="typeChooser" v-model="activeSearchType" class="style-chooser"
                            :options="searchTypes" :clearable="false" :disabled="disableSearching || !activeSearchType" />
                    </fg-input>
                </b-col>
                <b-col>
                    <fg-input :label="searchTypeLabel">
                        <v-select id="personChooser" v-model="object" class="style-chooser" :options="objects"
                            :get-option-label="optionLabel" :filterable="false" :clearable="false"
                            :disabled="disableSearchTypeLabel || !activeSearchType" @search="searchValueUpdated">
                            <template #option="object">
                                <div v-if="object.heading" class="heading">{{ object.label }}</div>
                                <div v-else style="text-wrap: wrap" v-html="optionLabel(object)" />
                            </template>
                            <template #selected-option="object">
                                <div style="text-wrap: wrap" v-html="optionLabel(object)" />
                            </template>
                        </v-select>
                    </fg-input>
                </b-col>
                <b-col cols="2" align-self="center">
                    <fg-input>
                        <el-date-picker v-model="fromDate" type="date" />
                    </fg-input>
                    <fg-input>
                        <el-date-picker v-model="toDate" type="date" />
                    </fg-input>
                </b-col>
                <b-col cols="2" align-self="center">
                    <b-button block size="sm" class="mb-1" @click="expandAll = !expandAll">
                        {{ expandAll ? "Collapse " : "Expand " }}
                        All
                    </b-button>
                    <b-button block size="sm" variant="primary" class="my-0" @click="handleExportPdf">
                        <i class="fa fa-file-pdf" />
                        Export to PDF
                    </b-button>
                    <b-button v-if="!disableSearching" variant="danger" block size="sm" class="mt-1"
                        @click="clearClicked">
                        Clear
                    </b-button>
                </b-col>
            </b-row>

            <div v-if="!activeSearchType" class="p-5 text-center">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Loading permissions...</strong>
            </div>
            <div v-else-if="!object" class="p-5 text-center">
                <strong>Select an object to view history.</strong>
            </div>
            <div v-else-if="loading" class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Loading...</strong>
            </div>
            <template v-else>
                <div v-if="allFields?.length" class="d-flex flex-wrap field-filters mt-1">
                    <b-badge :variant="filterFields.length ? 'secondary' : 'primary'" @click="filterFields = []">
                        All Fields
                    </b-badge>
                    <b-badge v-for="i in allFields" :key="i"
                        :variant="!filterFields.includes(i) ? 'secondary' : 'primary'" @click="toggleFilterField(i)">
                        {{ i }}
                    </b-badge>
                </div>

                <b-table id="history-table" :items="filteredDisplayHistory" :fields="tableFields" show-empty
                    :empty-text="allHistoriesHaveNoChanges ? 'No changes in history' : 'No history found'">
                    <template #cell(toggle)="row">
                        <a v-if="row.item.diff.length" href="#"
                            @click.prevent="row.toggleDetails">
                            <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': row.detailsShowing }"
                                aria-hidden="true" style="transition: all .2s;" />
                        </a>
                    </template>
                    <template #cell(updated)="row">
                        {{ row.value }}
                        <div v-if="row.item.isInitial">
                            <b-badge variant="success">
                                <i class="fa fa-plus" />
                                Initial Creation
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(fields)="row">
                        <div v-if="!row.detailsShowing" style="gap: 5px;" class="d-flex flex-wrap">
                            <b-badge v-for="i in getFilteredFieldsForDisplay(row.item.diff, row.item)" :key="i.key"
                                :variant="!filterFields.length || filterFields.includes(i.key) ? 'primary' : 'secondary'"
                                :title="getEnhancedTooltip(i)">
                                {{ i.key }}
                                <span v-if="i && i.key && isEntityIdField(i.key)" class="ml-1">
                                    <i class="fas fa-external-link-alt"></i>
                                </span>
                            </b-badge>
                        </div>
                    </template>
                    <template #row-details="row">
                        <b-card class="details-card">
                            <b-table :items="getValueDiffs(row.item.diff, row.item)"
                                :fields="['key', 'oldValue', 'newValue']" small>
                                <template #cell(oldValue)="data">
                                    <div
                                        v-if="data.item && data.item.key && isEntityIdField(data.item.key) && data.item.oldValue && getEntityLink(data.item.key, data.item.oldValue)">
                                        <router-link :to="getEntityLink(data.item.key, data.item.oldValue)"
                                            target="_blank">
                                            {{ getFormattedValue(data.item.key, data.item.oldValue, row.item) }}
                                            <i class="fas fa-external-link-alt ml-1"></i>
                                        </router-link>
                                    </div>
                                    <div v-else-if="getBitwiseExplanation(data.item.key, data.item.oldValue)"
                                        class="bitwise-value">
                                        <div>{{ data.item.oldValue }}</div>
                                        <div class="flag-list">
                                            <b-badge
                                                v-for="flag in getBitwiseExplanation(data.item.key, data.item.oldValue)"
                                                :key="`old-${flag}`" variant="danger" class="mr-1">
                                                {{ flag }}
                                            </b-badge>
                                        </div>
                                    </div>
                                    <div v-else>
                                        {{ getFormattedValue(data.item.key, data.item.oldValue, row.item) }}
                                    </div>
                                </template>
                                <template #cell(newValue)="data">
                                    <div
                                        v-if="data.item && data.item.key && isEntityIdField(data.item.key) && data.item.newValue && getEntityLink(data.item.key, data.item.newValue)">
                                        <router-link :to="getEntityLink(data.item.key, data.item.newValue)"
                                            target="_blank">
                                            {{ getFormattedValue(data.item.key, data.item.newValue, row.item) }}
                                            <i class="fas fa-external-link-alt ml-1"></i>
                                        </router-link>
                                    </div>
                                    <div v-else-if="getBitwiseExplanation(data.item.key, data.item.newValue)"
                                        class="bitwise-value">
                                        <div>{{ data.item.newValue }}</div>
                                        <div class="flag-list">
                                            <b-badge
                                                v-for="flag in getBitwiseExplanation(data.item.key, data.item.newValue)"
                                                :key="`new-${flag}`" variant="success" class="mr-1">
                                                {{ flag }}
                                            </b-badge>
                                        </div>
                                    </div>
                                    <div v-else>
                                        {{ getFormattedValue(data.item.key, data.item.newValue, row.item) }}
                                    </div>
                                </template>
                            </b-table>
                            <b-table :items="getArrayDiffs(row.item.diff, row.item)" :fields="['key', 'diff']" small>
                                <template #cell(diff)="data">
                                    <!-- Special handling for Program items (Flags, Teams, etc.) -->
                                    <template v-if="hasSpecialProgramHandling(data.item.key)">
                                        <template
                                            v-for="addedItem in getProgramItemChanges(row.item, data.item.key).added">
                                            <card plain class="p-2"
                                                :key="`added-${getIdProperty(addedItem, 'FunctionalityFlagId', 'functionalityFlagId', 'TeamId', 'teamId', 'Id', 'id', 'drugProductId', 'DrugProductId')}`">
                                                <b-badge variant="success">Added Entry</b-badge>
                                                <br>
                                                {{ formatEntry(addedItem, data.item.key) }}
                                            </card>
                                        </template>
                                        <template
                                            v-for="removedItem in getProgramItemChanges(row.item, data.item.key).removed">
                                            <card plain class="p-2"
                                                :key="`removed-${getIdProperty(removedItem, 'FunctionalityFlagId', 'functionalityFlagId', 'TeamId', 'teamId', 'Id', 'id', 'drugProductId', 'DrugProductId')}`">
                                                <b-badge variant="danger">Removed Entry</b-badge>
                                                <br>
                                                {{ formatEntry(removedItem, data.item.key) }}
                                            </card>
                                        </template>
                                        <!-- Show updated items only for DrugPrograms with meaningful changes -->
                                        <template v-if="data.item.key === 'DrugPrograms'"
                                            v-for="updatedItem in getProgramItemChanges(row.item, data.item.key).updated">
                                            <card plain class="p-2"
                                                :key="`updated-${getIdProperty(updatedItem.new, 'packageId', 'PackageId', 'drugProductId', 'DrugProductId', 'Id', 'id')}`">
                                                <b-badge variant="info">Updated Entry</b-badge>
                                                <br>
                                                {{ formatEntry(updatedItem.new, data.item.key) }}
                                                <div v-if="updatedItem.changes && updatedItem.changes.length > 0">
                                                    <hr>
                                                    <strong>Meaningful Changes:</strong>
                                                    <ul style="margin: 0; padding-left: 20px;">
                                                        <li v-for="change in updatedItem.changes"
                                                            :key="change">{{ change }}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </card>
                                        </template>
                                    </template>
                                    <!-- Default handling for other array types -->
                                    <template v-else>
                                        <card v-for="i in getFilteredArrayKeys(data.value)" :key="i" plain
                                            class="p-2">
                                            <template v-if="Array.isArray(data.value[i]) && data.value[i].length === 1">
                                                <b-badge variant="success">Added Entry</b-badge>
                                                <br>
                                                {{ getAddedEntry(row.item, data.item.key, i, data.value[i][0]) }}
                                            </template>
                                            <template
                                                v-else-if="Array.isArray(data.value[i]) && data.value[i][1] === 0 && data.value[i][2] === 0">
                                                <b-badge variant="danger">Removed Entry</b-badge>
                                                <br>
                                                {{ getRemovedEntry(row.item, data.item.key, i, data.value[i][0]) }}
                                            </template>
                                            <template v-else-if="Array.isArray(data.value[i])">
                                                <b-badge variant="info">Moved Entry</b-badge>
                                                <br>
                                                {{ formatEntry(data.value[i][0], data.item.key) }}
                                            </template>
                                            <template v-else>
                                                <b-badge variant="info">Updated Entry</b-badge>
                                                <br>
                                                <template v-if="row.item.newValue[data.item.key][i]">
                                                    {{ formatEntry(row.item.newValue[data.item.key][i],
                                                    data.item.key) }}
                                                </template>

                                                <hr>
                                                Changes:<br>
                                                <pre style="white-space: pre-wrap; font-size: 0.9em;">
                                                    {{ JSON.stringify(data.value[i], null, 2) }}
                                                </pre>
                                            </template>
                                        </card>
                                    </template>
                                </template>
                            </b-table>
                        </b-card>
                    </template>
                </b-table>

                <table v-show="false" id="history-export">
                    <thead>
                    <tr>
                        <th>Key</th>
                        <th>Old Value</th>
                        <th>New Value</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="h in filteredDisplayHistory">
                        <tr :key="`${h?.id}-${h?.updated}`">
                            <th colspan="3">
                                <strong>
                                    {{ h?.updatedBy }}
                                    at
                                    {{ dateFormatter(h?.updated) }}
                                </strong>
                            </th>
                        </tr>
                        <template v-for="hi in h?.diff">
                            <tr :key="`${hi.key}-${h?.updated}`">
                                <td>{{ hi.key }}</td>
                                <td>{{ hi.oldValue }}</td>
                                <td>{{ hi.newValue }}</td>
                            </tr>
                        </template>
                    </template>
                    </tbody>
                </table>

            </template>
        </form>
    </component>
</template>

<script lang="ts">
import { Component, Mixins, Prop, VModel, Watch } from 'vue-property-decorator';
import { Person } from '@/models/Person';
import { History, historyDiff } from '@/models/History';
import axios, { CancelTokenSource } from 'axios';
import { DatePicker } from 'element-ui';
import moment from 'moment';
import { debounce } from 'throttle-debounce';
import { ReportExporter } from "@/mixins/ReportExporter";
import HasLabel from "@/models/HasLabel";
import SearchComponent from "@/components/SearchComponent.vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { PriorAuthorizationType } from '@/models/PriorAuthorization';
import { BillingFlags, Plan } from '@/models/Plan';
import { StoreCapabilities } from '@/models/Store';
import { RefillStatusCode, OriginCode, DAWCode } from '@/models/Prescription';
import { DrugSource } from '@/models/Drug/DrugProduct';
import { DeliveryCode } from '@/models/DeliveryCode';
import { Language } from '@/models/Patient';
import { OrderStatus } from '@/models/Order';

interface SubStatus {
    id: number | null;
    name: string;
    active: boolean;
    position: number;
}

interface HistoryObject {
    id: number;
    name?: string;
    storeID?: number;
    rxNumber?: string;
    rfNumber?: string;
    patientID?: number;
    userName?: string;
    title?: string;
    heading?: boolean;
    label?: string;
}

interface SearchType {
    label?: string;
    value?: string;
    key?: string;
}

interface DrugPackageDetails {
    name: string;
    source: DrugSource;
}

@Component({
    name: "HistoryComponent", components: {
        SearchComponent, [DatePicker.name]: DatePicker,
    },
})
export default class HistoryComponent extends Mixins(ReportExporter) {

    private parseHistoryValues(historyItem: History): { oldValue: any, newValue: any } {
        try {
            const oldValue = JSON.parse(historyItem.oldValue)[0];
            const newValue = historyItem.newValue;
            return { oldValue, newValue };
        } catch (error) {
            console.warn('Error parsing history values:', error);
            return { oldValue: null, newValue: null };
        }
    }

    private getIdProperty(item: HistoryObject, ...propertyNames: string[]): number | null {
        for (const prop of propertyNames) {
            const val = (item as any)[prop];
            if (val !== undefined && typeof val === 'number') {
                return val;
            }
        }
        return null;
    }

    private object: HistoryObject | null = null;
    protected objects: HistoryObject[] = [];

    private fromDate: string = moment().subtract(7, 'd').toDate().toDateString();
    private toDate: string = moment().endOf('day').toDate().toDateString();

    private readonly allSearchTypes: Array<string | SearchType> = [
        "Patient",
        "Prescriber",
        "Vendor",
        "DrugCategory",
        "Store",
        "Prescription",
        "User",
        "Order",
        "AutoRefill",
        "Plan",
        { label: "Configuration", value: "Configuration" },
        { label: "Program", value: "Program" },
        { label: "Team", value: "User/Teams" },
        { label: "Task", value: "Task", key: "tasks" },
        { label: "Custom Text Template", value: "CustomTextMessage" },
        "Case",
        { label: "Task Trigger", value: "Task/Triggers" },
        { label: "Prior Authorization", value: "PriorAuthorization" },
        { label: "Prescriber Contact Template", value: "Prescriber/contact-templates" },
        { label: "Prescriber Contact Triggers", value: "Prescriber/contact-templates/triggers" },
    ];

    /**
     * Maps entity types to their required permissions.
     * Returns the permission code name required to view history for a given entity type.
     */
    private getPermissionForEntityType(searchType: string | SearchType): string | null {
        // Extract the label or value from SearchType objects
        const typeKey = typeof searchType === 'string' ? searchType : (searchType.label || searchType.value || '');

        // Map entity types to their required permissions
        const permissionMap: Record<string, string> = {
            'Patient': 'Patient Dashboard/View',
            'Prescriber': 'Maintenance/Doctor',
            'Vendor': 'Maintenance/Vendor',
            'DrugCategory': 'Admin/Category',
            'Store': 'Maintenance/Store',
            'Prescription': 'Patient Dashboard/View',
            'User': 'Admin/Users',
            'Order': 'Patient Dashboard/View',
            'AutoRefill': 'Patient Dashboard/View',
            'Plan': 'Admin/Plan',
            'Configuration': 'Admin/Configuration',
            'Program': 'Admin/Program',
            'Team': 'Admin/Teams',
            'Task': 'Processing/Tasks',
            'Custom Text Template': 'Admin/Custom Text Message Templates',
            'Case': 'Compliance/View Cases',
            'Task Trigger': 'Admin/Task Triggers',
            'Prior Authorization': 'Services/Prior Authorization',
            'Prescriber Contact Template': 'Admin/Prescriber Contact Templates',
            'Prescriber Contact Triggers': 'Admin/Prescriber Contact Templates',
        };

        return permissionMap[typeKey] || null;
    }

    /**
     * Checks if the user has permission to view history for a given entity type.
     */
    private hasPermissionForEntityType(searchType: string | SearchType): boolean {
        const requiredPermission = this.getPermissionForEntityType(searchType);

        // If no permission is mapped, allow by default (for backward compatibility)
        if (!requiredPermission) {
            return true;
        }

        // Check if user has the required permission
        return this.$permissions.some(p => p.permissionCodeName === requiredPermission);
    }

    /**
     * Computed property that filters search types based on user permissions.
     */
    get searchTypes(): Array<string | SearchType> {
        return this.allSearchTypes.filter(searchType => this.hasPermissionForEntityType(searchType));
    }

    private histories: History[] = [];
    private expandAll: boolean = false;
    private filterFields: string[] = [];
    protected loading = false;
    // Generic cache infrastructure - made reactive
    private entityCache: Record<string, Record<number, any>> = {};
    private loadingEntities: Record<string, Set<number>> = {};
    private cacheUpdateTrigger: number = 0; // Reactive trigger for cache updates
    private readonly maxCacheSize = 1000; // Prevent memory leaks
    private cleanupInterval: number | null = null;
    private pendingRequests: Map<string, Promise<any>> = new Map(); // Request deduplication

    // Legacy cache for migration
    private functionalityFlags: Map<number, string> = new Map();
    private teams: Map<number, string> = new Map();

    // SubStatus caches - separate from generic cache system for specific use case
    private onHoldSubStatusCache: Record<number, string> = {};
    private discontinuedSubStatusCache: Record<number, string> = {};
    private loadingOnHoldSubStatuses: boolean = false;
    private loadingDiscontinuedSubStatuses: boolean = false;

    @VModel({ default: null }) private passedObject!: HistoryObject | null;
    @Prop({ type: String, default: "Patient" }) protected searchType!: string;
    private activeSearchType: string | SearchType | null = null;

    @Watch('passedObject', { immediate: true })
    onPassedObjectUpdate(value: HistoryObject | null, _oldValue: HistoryObject | null) {
        if (this.object === value) return;
        this.object = value;
        if (this.object === null) {
            this.objects = [];
        } else {
            this.objects = [ this.object ];
        }
    }

    @Watch('searchTypes', { immediate: true })
    onSearchTypesUpdate(value: Array<string | SearchType>, _oldValue: Array<string | SearchType>) {
        // This watcher fires when permissions are loaded and searchTypes becomes available
        if (!value || value.length === 0) {
            return;
        }

        // Initialize or update activeSearchType based on the searchType prop
        const foundMatch = value.find(st => st === this.searchType || (st as SearchType).label === this.searchType);

        // If the requested searchType isn't permitted, warn and use first available
        if (!foundMatch && this.activeSearchType === null) {
            const existsInAllTypes = this.allSearchTypes.find(st => st === this.searchType || (st as SearchType).label === this.searchType);
            if (existsInAllTypes) {
                console.warn(`User does not have permission to view ${this.searchType} history`);
            }
        }

        this.activeSearchType = foundMatch ?? value[0];
    }

    @Watch('searchType')
    onSearchTypeUpdate(value: string, _oldValue: string) {
        // When searchType prop changes (not on initial load), update activeSearchType
        if (!this.searchTypes || this.searchTypes.length === 0) {
            return;
        }

        const foundMatch = this.searchTypes.find(st => st === value || (st as SearchType).label === value);
        this.activeSearchType = foundMatch ?? this.searchTypes[0];
    }

    mounted() {
        // Set up periodic cache cleanup
        this.cleanupInterval = window.setInterval(() => {
            this.cleanupCache();
        }, 300000); // Clean up every 5 minutes
    }

    beforeDestroy() {
        // Clean up to prevent memory leaks
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }

        // Clear all caches and pending requests
        this.entityCache = {};
        this.loadingEntities = {};
        this.pendingRequests.clear();
        this.functionalityFlags.clear();
        this.teams.clear();
        this.onHoldSubStatusCache = {};
        this.discontinuedSubStatusCache = {};
    }

    get searchTypeLabel() {
        if (!this.activeSearchType) {
            return 'Loading...';
        }
        if (typeof this.activeSearchType === 'string') {
            return this.activeSearchType;
        }
        return (this.activeSearchType as SearchType).label ?? this.activeSearchType;
    }

    get disableSearching() {  // disable all dropdowns
        return this.passedObject != null && (this.passedObject == this.object || this.passedObject.id === this.object?.id);
    }

    get disableSearchTypeLabel() {
        return this.object as unknown === 'configuration' || this.disableSearching;
    }

    private readonly optionLabelFormatters: Record<string, (option: HistoryObject) => string> = {
        "Task": (opt: HistoryObject) => `${ opt.title } (Id: ${ opt.id })`,
        "Order": (opt: HistoryObject) => `Order Id: ${ opt.id }`,
        "AutoRefill": (opt: HistoryObject) => `Subscription: ${ opt.id } Prescription: ${ opt.storeID }-${ opt.rxNumber }`,
        "Patient": (opt: HistoryObject) => new Person(opt as Person).toString(),
        "Plan": (opt: any) => `${opt.name} (Id: ${opt.id})${opt.bin ? ` - BIN: ${opt.bin}` : ''}${opt.pcn ? ` - PCN: ${opt.pcn}` : ''}`,
        "Prescriber Contact Triggers": (opt: HistoryObject) => `${ (opt as any).prescriberContactTemplate.name } (Id: ${ opt.id })`,
        "Case": (opt: HistoryObject) => `Id: ${ opt.id }`,
        "Prior Authorization": (opt: HistoryObject) => `Id: ${ opt.id } - Prescription: ${ (opt as any).prescription.rxId }${ (opt as any).type != null ? ` - Type: ${ PriorAuthorizationType[(opt as any).type] }` : '' }`,
    };

    optionLabel(option: HistoryObject): string {
        const searchTypeKey = typeof this.activeSearchType === 'string' ? this.activeSearchType : this.activeSearchType.label!;
        const formatter = this.optionLabelFormatters[searchTypeKey];

        if (formatter) {
            return formatter(option);
        }

        // Default fallback logic
        if (option.name != null) return option.name;
        if (option.storeID) return `Store-Rx-Rf: ${ option.storeID }-${ option.rxNumber }-${ option.rfNumber }, Patient:${ option.patientID }`;
        if (option.userName) return option.userName;
        if (option.title) return option.title;
        if (option as any instanceof HasLabel) return (option as any as HasLabel).getLabel();

        return new Person(option as Person).toString();
    }

    formatDelta(delta: any, historyItem?: any): any[] {
        const formattedChanges: any[] = [];
        if (!delta) return formattedChanges;

        Object.entries(delta).forEach(([ key, value ]) => {
            if (key === '_t' && value === 'a') {
                // This object itself represents array changes, process its numeric/underscore keys
                // Example: handle insertions (_0), deletions (_1=[old,0,0]), modifications (1: {nested diff})
                // This part requires careful handling based on jsondiffpatch array diff format
                // For simplicity, we'll skip detailed array diff formatting here,
                // but you would add logic to interpret keys like '_0', '1', etc.
            } else if (Array.isArray(value)) {
                if (value.length === 1) {
                    // Likely an addition
                    formattedChanges.push({ key, newValue: value[0], type: 'add' });
                } else if (value.length === 2) {
                    // Simple value change
                    if (value[0] !== value[1]) {
                        formattedChanges.push({ key, oldValue: value[0], newValue: value[1], type: 'modify' });
                    }
                } else if (value.length === 3 && value[1] === 0 && value[2] === 0) {
                    // Likely a deletion
                    formattedChanges.push({ key, oldValue: value[0], type: 'delete' });
                }
                // Note: This simplistic check might misinterpret complex array changes
                // A robust solution needs to check for the _t: 'a' marker on the parent object.

            } else if (typeof value === 'object' && value !== null) {
                // Nested object change - recurse or format as needed
                // Check if it's an array change marker object itself
                if ((value as any)._t === 'a') {
                    // Special handling for DrugPrograms - check if changes are meaningful
                    if (key === 'DrugPrograms' && historyItem) {
                        const drugProgramChanges = this.getDrugProgramChanges(historyItem);
                        if (drugProgramChanges.hasMeaningfulChanges) {
                            formattedChanges.push({ key: key, diff: value, type: 'array_change' });
                        }
                        // If no meaningful changes, don't add to formattedChanges
                    } else {
                        formattedChanges.push({ key: key, diff: value, type: 'array_change' }); // Mark as array change
                    }
                } else {
                    // Treat as nested object changes
                    const nestedChanges = this.formatDelta(value, historyItem);
                    if (nestedChanges.length > 0) {
                        formattedChanges.push({ key: key, nested: nestedChanges, type: 'object_change' });
                    }
                }
            }
        });
        return formattedChanges;
    }

    diffForHistory(history: History, forTable: boolean = false): any {
        const rawDelta = historyDiff(history); // Keep using the original historyDiff
        if (!forTable) return rawDelta; // Return raw delta if not for table display

        if (rawDelta && Object.keys(rawDelta).length === 0) return []; // Handle empty delta

        // Handle the "Initial Creation" case specifically if needed
        if (rawDelta && rawDelta[0] && !rawDelta[1]) {
            const ogDelta = historyDiff({ oldValue: "[{}]", newValue: history.newValue } as History);
            return { data: this.formatDelta(ogDelta, history), isInitial: true };
        }

        // Use the enhanced formatter
        return { data: this.formatDelta(rawDelta, history) };
    }

    @Watch('activeSearchType')
    onActiveSearchTypeUpdate(_value: any, _oldValue: any) {
        this.objects = [];
        this.object = null;
        if ((this.activeSearchType as SearchType).value === "Configuration") {
            this.getHistoryForObject('Configuration');

            this.object = 'configuration' as any;
        } else if ((this.activeSearchType as SearchType).value === "Program") {
            Promise.all([ this.loadFunctionalityFlags(), this.loadTeams() ]);
        }
    }

    @Watch('object')
    onObjectUpdate(value: HasLabel, _oldValue: HasLabel) {
        if (!value) return;
        this.getHistoryForObject(value);
    }

    @Watch('fromDate')
    onFromDateUpdate(_value: any, _oldValue: any) {
        if (!this.object) return;
        this.getHistoryForObject(this.object);

    }

    @Watch('toDate')
    onToDateUpdate(_value: any, _oldValue: any) {
        if (!this.object) return;
        this.getHistoryForObject(this.object);
    }

    @Watch('allFields')
    onAllFieldsUpdate() {
        if (!this.filterFields.length) return;
        this.filterFields = this.filterFields.filter(ff => this.allFields.includes(ff));
    }

    clearClicked() {
        this.object = null;
        this.histories = [];
    }

    public async getHistoryForObject(value: any) {
        const isValid = value && (value.id || value.storeID || value.userName || value === 'Configuration');
        if (!isValid) return;

        let id = value.id;
        if (this.activeSearchType == "Prescription") {
            id = `${ value.storeID }-${ value.rxNumber }-${ value.rfNumber }`;
        } else if (this.activeSearchType == "User") {
            id = value.userName;
        }

        const searchType = (this.activeSearchType as SearchType).value ?? this.activeSearchType;
        const basePath = `/${ searchType }`;

        const searchPath = this.activeSearchType === "Configuration" ? encodeURI(`${ basePath }/history`) : id ? encodeURI(
            `${ basePath }/${ id }/history`) : encodeURI(`${ basePath }/history`);
        const config = {
            params: {
                fromDate: moment(this.fromDate).startOf('day').utc().toDate(),
                toDate: moment(this.toDate).endOf('day').utc().toDate(),
            },
        };

        try {
            this.loading = true;
            const response = await axios.get(searchPath, config);
            const histories = response.data as History[];

            if (histories.length === 0) {
                this.histories = [];
            } else {
                // Process histories and mark those with no meaningful changes
                this.histories = histories.map((value) => {
                    const diff = this.diffForHistory(value);
                    if (!diff || (typeof diff === 'object' && Object.keys(diff).length === 0)) {
                        value.noMeaningfulChanges = true;
                    }
                    return value;
                });
            }
        } catch (error) {
            console.warn('Failed to load history:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    private throttledSearch: Function = debounce(500, (searchValue) => {
        this.onSearch(searchValue);
    });

    async searchValueUpdated(value: string) {
        if (!value.length) return;
        this.throttledSearch(value);
    }

    private cancelTokenSource: CancelTokenSource | null = null;
    private searching = false;

    get searchPath(): string {
        return `/${ (this.activeSearchType as SearchType).value ?? this.activeSearchType }`;
    }

    async onSearch(value: string) {
        if (this.searching) {
            this.cancelTokenSource?.cancel("New search started");
            this.searching = false;
        }

        if (!value.length) return;

        this.cancelTokenSource = axios.CancelToken.source();

        const config = {
            params: { searchTerm: value }, cancelToken: this.cancelTokenSource?.token,
        };

        this.searching = true;

        try {
            const response = await axios.get(this.searchPath, config);

            let objects = response.data;
            if ((this.activeSearchType as SearchType).key) objects = objects[(this.activeSearchType as SearchType).key ?? ''];
            if (!Array.isArray(objects)) objects = [ objects ];

            this.objects = objects;
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.searching = false;
        }
    }

    get displayHistory() {
        return this.histories?.map(h => {
            // Skip entries with no meaningful changes
            if (h.noMeaningfulChanges) return null;

            const { data, isInitial } = this.diffForHistory(h, true);
            return { ...h, isInitial, newValue: JSON.parse(h.newValue)[0], diff: data, _showDetails: this.expandAll };
        }).filter(h => {
            // Filter out null entries (those with no meaningful changes)
            if (!h) return false;

            // Filter out history entries that have no meaningful fields after DrugPrograms filtering
            if (h.diff.length === 0) return false;

            // Check if all fields are filtered out (no meaningful changes)
            const meaningfulFields = this.getFilteredFieldsForDisplay(h.diff, h);
            return meaningfulFields.length > 0;
        });
    }

    get filteredDisplayHistory() {
        if (!this.filterFields.length) return this.displayHistory;

        return this.displayHistory.filter(dh => {
            // Check if any of the selected filter fields are present AND meaningful
            return this.filterFields.some(filterField => {
                // For DrugPrograms, check if it has meaningful changes
                if (filterField === 'DrugPrograms') {
                    const hasDrugPrograms = dh?.diff.some((d: any) => d.key === 'DrugPrograms');
                    if (hasDrugPrograms) {
                        const drugProgramChanges = this.getDrugProgramChanges(dh);
                        return drugProgramChanges.hasMeaningfulChanges;
                    }
                    return false;
                }

                // For other fields, check if they exist in the diff
                return dh?.diff.some((d: any) => d.key === filterField);
            });
        });
    }

    get tableFields() {
        return [
            { key: 'toggle', label: '', class: 'toggle-col' },
            { key: 'updated', formatter: this.dateTimeFormatter },
            'updatedBy',
            { key: 'fields', label: 'Updated Fields' },
        ];
    }

    // Computed property for program change handlers
    private readonly programChangeHandlers: Record<string, (historyItem: any) => any> = {
        'ProgramFunctionalityFlags': this.getProgramFlagChanges,
        'ProgramTeams': this.getProgramTeamChanges,
        'DrugPrograms': this.getDrugProgramChanges,
    };

    dateTimeFormatter(d: any) {
        const date = moment.utc(d).local();
        return `${ date.format("L LT") } (${ date.fromNow() })`;
    }

    dateFormatter(d: any) {
        if (!d) return "";
        const date = moment.utc(d).local();
        return `${ date.format("L LT") } (${ date.fromNow() })`;
    }

    get allFields() {
        const allFieldsSet = new Set<string>();

        this.displayHistory?.forEach(dh => {
            dh?.diff.forEach((d: { key: string }) => {
                // For DrugPrograms, only add to available fields if ANY history has meaningful changes
                if (d.key === 'DrugPrograms') {
                    const drugProgramChanges = this.getDrugProgramChanges(dh);
                    if (drugProgramChanges.hasMeaningfulChanges) {
                        allFieldsSet.add(d.key);
                    }
                } else {
                    allFieldsSet.add(d.key);
                }
            });
        });

        return [ ...allFieldsSet ];
    }

    get allHistoriesHaveNoChanges() {
        return this.histories.length > 0 && this.histories.every((h: any) => h.noMeaningfulChanges);
    }

    toggleFilterField(field: string) {
        if (this.filterFields.includes(field)) {
            this.filterFields = this.filterFields.filter(ff => ff !== field);
        } else {
            this.filterFields.push(field);
        }
    }

    async handleExportPdf() {
        const jspdf = await import(/* webpackChunkName: "jspdf" */ "jspdf");
        const autotable = await import(/* webpackChunkName: "jspdf" */ "jspdf-autotable");

        const doc = new jspdf.default();
        doc.addFont('ArialMS', 'Arial', 'normal');
        doc.addFont('ArialMS', 'Arial', 'bold');

        doc.setFontSize(18);
        doc.setFont('Arial', 'bold');
        doc.text(`${ this.activeSearchType } History Report`, 14, 22);
        doc.setFontSize(14);
        doc.setFont('Arial', 'normal');
        doc.text(this.optionLabel(this.object!), 14, 30);

        doc.setFontSize(10);
        doc.text(`Generated on ${ moment().format("L LT") } (${ moment().fromNow() })`, 14, 37);

        autotable.default(doc, {
            html: "#history-export",
            includeHiddenHtml: false,
            showFoot: 'never',
            theme: "grid",
            headStyles: {
                fillColor: '#124e80',
            },
            styles: { cellWidth: 'wrap', overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 65 }, 2: { cellWidth: 65 } },
            didParseCell(data) {
                const text = data.cell.raw;
                if (!(text as HTMLTableCellElement).querySelector('strong')) return;

                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = '#cee3f5';
            },
            startY: 42,
        });

        let objectId = 0;
        if ((this.object as any)?.id) {
            objectId = (this.object as any)?.id;
        }
        doc.save(`${ this.activeSearchType } History Report - ${ objectId }.pdf`);
    }

    getArrayDiffs(items: Array<any>, historyItem?: any) {
        return items.filter((d: any) => {
            if (d.type !== 'array_change') return false;

            // Filter out DrugPrograms if there are no meaningful changes
            if (d.key === 'DrugPrograms' && historyItem) {
                const drugProgramChanges = this.getDrugProgramChanges(historyItem);
                return drugProgramChanges.hasMeaningfulChanges;
            }

            return true;
        });
    }

    getValueDiffs(items: Array<any>, historyItem?: any) {
        return items.filter((d: any) => {
            if (d.type === 'array_change') return false;

            // Filter out DrugPrograms if there are no meaningful changes
            if (d.key === 'DrugPrograms' && historyItem) {
                const drugProgramChanges = this.getDrugProgramChanges(historyItem);
                return drugProgramChanges.hasMeaningfulChanges;
            }

            return true;
        });
    }

    getFilteredFieldsForDisplay(items: Array<any>, historyItem?: any) {
        return items.filter((d: any) => {
            // Filter out DrugPrograms if there are no meaningful changes
            if (d.key === 'DrugPrograms' && historyItem) {
                const drugProgramChanges = this.getDrugProgramChanges(historyItem);
                return drugProgramChanges.hasMeaningfulChanges;
            }

            return true;
        });
    }

    isRealChange(value: any): boolean {
        if (Array.isArray(value)) {
            return true;
        }

        if (typeof value === 'object' && value !== null) {
            const keys = Object.keys(value);

            if (keys.length === 1 && keys[0] === 'FunctionalityFlagId' && Array.isArray(value.FunctionalityFlagId) && value.FunctionalityFlagId.length === 2) {
                return false;
            }

            return true;
        }

        return true;
    }

    getFilteredArrayKeys(diffValue: any): string[] {
        return Object.keys(diffValue).filter(key => {
            if (key === '_t') return false;

            const value = diffValue[key];
            return this.isRealChange(value);
        });
    }

    getRemovedFlagEntry(historyItem: any, diffKey: string, _arrayIndex?: string): any {
        if (diffKey !== 'ProgramFunctionalityFlags') return null;

        try {
            const oldValue = JSON.parse(historyItem.oldValue)[0];
            const newValue = historyItem.newValue;

            const oldFlags = oldValue?.[diffKey] || [];
            const newFlags = newValue?.[diffKey] || [];

            const newFlagIds = new Set(newFlags.map((f: any) => f.FunctionalityFlagId || f.functionalityFlagId));
            const removedFlags = oldFlags.filter((oldFlag: any) => {
                const flagId = oldFlag.FunctionalityFlagId || oldFlag.functionalityFlagId;
                return !newFlagIds.has(flagId);
            });

            return removedFlags.length > 0 ? removedFlags[0] : null;
        } catch (error) {
            console.warn('Error identifying removed flag:', error);
            return null;
        }
    }

    getAddedFlagEntry(historyItem: any, diffKey: string, arrayIndex?: string): any {
        if (diffKey !== 'ProgramFunctionalityFlags') return null;

        try {
            const oldValue = JSON.parse(historyItem.oldValue)[0];
            const newValue = historyItem.newValue;

            const oldFlags = oldValue?.[diffKey] || [];
            const newFlags = newValue?.[diffKey] || [];

            if (arrayIndex !== undefined && !isNaN(parseInt(arrayIndex))) {
                const index = parseInt(arrayIndex);
                const flagAtPosition = newFlags[index];

                if (flagAtPosition) {
                    const flagId = flagAtPosition.FunctionalityFlagId || flagAtPosition.functionalityFlagId;
                    const oldFlagIds = new Set(oldFlags.map((f: any) => f.FunctionalityFlagId || f.functionalityFlagId));

                    if (!oldFlagIds.has(flagId)) {
                        return flagAtPosition;
                    }
                }
            }

            const oldFlagIds = new Set(oldFlags.map((f: any) => f.FunctionalityFlagId || f.functionalityFlagId));
            const addedFlags = newFlags.filter((newFlag: any) => {
                const flagId = newFlag.FunctionalityFlagId || newFlag.functionalityFlagId;
                return !oldFlagIds.has(flagId);
            });

            return addedFlags.length > 0 ? addedFlags[0] : null;
        } catch (error) {
            console.warn('Error identifying added flag:', error);
            return null;
        }
    }

    getFormattedEntry(historyItem: any, diffKey: string, arrayIndex: string, fallbackValue: any, isRemoved: boolean = false): string {
        let entry = fallbackValue;

        if (diffKey === 'ProgramFunctionalityFlags') {
            entry = (isRemoved ? this.getRemovedFlagEntry(historyItem, diffKey, arrayIndex) : this.getAddedFlagEntry(
                historyItem,
                diffKey,
                arrayIndex)) || fallbackValue;
        }

        return this.formatEntry(entry, diffKey);
    }

    getAddedEntry(historyItem: any, diffKey: string, arrayIndex: string, fallbackValue: any): string {
        return this.getFormattedEntry(historyItem, diffKey, arrayIndex, fallbackValue, false);
    }

    getRemovedEntry(historyItem: any, diffKey: string, arrayIndex: string, fallbackValue: any): string {
        return this.getFormattedEntry(historyItem, diffKey, arrayIndex, fallbackValue, true);
    }

    private getProgramChanges(historyItem: any, arrayKey: string, idProperties: string[]): {
        added: any[], removed: any[]
    } {
        try {
            const { oldValue, newValue } = this.parseHistoryValues(historyItem);
            if (!oldValue || !newValue) return { added: [], removed: [] };

            const oldItems = oldValue[arrayKey] || [];
            const newItems = newValue[arrayKey] || [];

            const oldItemIds = new Set(oldItems.map((item: any) => this.getIdProperty(item, ...idProperties)));
            const addedItems = newItems.filter((newItem: any) => {
                const itemId = this.getIdProperty(newItem, ...idProperties);
                return !oldItemIds.has(itemId);
            });

            const newItemIds = new Set(newItems.map((item: any) => this.getIdProperty(item, ...idProperties)));
            const removedItems = oldItems.filter((oldItem: any) => {
                const itemId = this.getIdProperty(oldItem, ...idProperties);
                return !newItemIds.has(itemId);
            });

            return { added: addedItems, removed: removedItems };
        } catch (error) {
            console.warn(`Error getting program ${ arrayKey } changes:`, error);
            return { added: [], removed: [] };
        }
    }

    getProgramFlagChanges(historyItem: any): { added: any[], removed: any[], updated: any[] } {
        const arrayKey = 'ProgramFunctionalityFlags';
        const idProperties = [ 'FunctionalityFlagId', 'functionalityFlagId' ];
        const result = this.getProgramChanges(historyItem, arrayKey, idProperties);
        return { ...result, updated: [] };
    }

    getProgramTeamChanges(historyItem: any): { added: any[], removed: any[], updated: any[] } {
        const result = this.getProgramChanges(historyItem, 'ProgramTeams', [ 'TeamId', 'teamId' ]);
        return { ...result, updated: [] };
    }

    getDrugProgramChanges(historyItem: any): {
        added: any[], removed: any[], updated: any[], hasMeaningfulChanges: boolean
    } {
        try {
            const { oldValue, newValue } = this.parseHistoryValues(historyItem);
            if (!oldValue || !newValue) return { added: [], removed: [], updated: [], hasMeaningfulChanges: false };

            const oldDrugPrograms = oldValue.DrugPrograms || [];
            const newDrugPrograms = newValue.DrugPrograms || [];

            // Helper to get unique ID for a drug program entry
            const getEntryId = (entry: any) => {
                return entry.packageId || entry.PackageId || entry.drugProductId || entry.DrugProductId || entry.id || entry.Id;
            };

            // Build maps for comparison
            const oldMap = new Map(oldDrugPrograms.map((item: any) => [ getEntryId(item), item ]));
            const newMap = new Map(newDrugPrograms.map((item: any) => [ getEntryId(item), item ]));

            // Find added items
            const addedItems = newDrugPrograms.filter((item: any) => !oldMap.has(getEntryId(item)));

            // Find removed items
            const removedItems = oldDrugPrograms.filter((item: any) => !newMap.has(getEntryId(item)));

            // Find updated items (exist in both but have meaningful changes)
            const updatedItems: any[] = [];
            newDrugPrograms.forEach((newItem: any) => {
                const id = getEntryId(newItem);
                const oldItem = oldMap.get(id);

                if (oldItem && this.hasMeaningfulDrugProgramChanges(oldItem, newItem)) {
                    const updateItem = {
                        old: oldItem, new: newItem, changes: this.getDrugProgramChangeSummary(oldItem, newItem),
                    };
                    updatedItems.push(updateItem);
                }
            });

            // Check if there are any meaningful changes at all
            const hasMeaningfulChanges = addedItems.length > 0 || removedItems.length > 0 || updatedItems.length > 0;

            return { added: addedItems, removed: removedItems, updated: updatedItems, hasMeaningfulChanges };
        } catch (error) {
            console.warn('Error getting DrugPrograms changes:', error);
            return { added: [], removed: [], updated: [], hasMeaningfulChanges: false };
        }
    }

    hasMeaningfulDrugProgramChanges(oldItem: any, newItem: any): boolean {
        // Fields that we consider non-meaningful
        const ignoredFields = [ 'updateDate', 'updateBy', 'updatedDate', 'updatedBy', 'updateTime', 'updatedTime' ];

        // Check if there are any changes besides the ignored fields
        for (const key in newItem) {
            if (ignoredFields.some(field => key.toLowerCase() === field.toLowerCase())) {
                continue;
            }

            if (JSON.stringify(oldItem[key]) !== JSON.stringify(newItem[key])) {
                return true;
            }
        }

        return false;
    }

    getDrugProgramChangeSummary(oldItem: any, newItem: any): string[] {
        const changes: string[] = [];
        const ignoredFields = [ 'updateDate', 'updateBy', 'updatedDate', 'updatedBy', 'updateTime', 'updatedTime' ];

        for (const key in newItem) {
            if (ignoredFields.some(field => key.toLowerCase() === field.toLowerCase())) {
                continue;
            }

            if (JSON.stringify(oldItem[key]) !== JSON.stringify(newItem[key])) {
                changes.push(`${ key }: ${ oldItem[key] } → ${ newItem[key] }`);
            }
        }

        return changes;
    }

    hasSpecialProgramHandling(key: string): boolean {
        return key in this.programChangeHandlers;
    }

    getProgramItemChanges(historyItem: any, key: string): any {
        const handler = this.programChangeHandlers[key];
        return handler ? handler(historyItem) : { added: [], removed: [], updated: [] };
    }

    formatEntry(entry: any, key: string): string {
        if (typeof entry !== 'object' || entry === null) {
            return String(entry);
        }

        // Handle program-specific entries
        switch (key) {
            case 'ProgramFunctionalityFlags': {
                const idProperties = [ 'FunctionalityFlagId', 'functionalityFlagId' ];
                return this.formatProgramEntry(entry, idProperties, this.functionalityFlags, 'Flag ID');
            }
            case 'ProgramTeams': {
                const idProperties = [ 'TeamId', 'teamId' ];
                return this.formatProgramEntry(entry, idProperties, this.teams, 'Team ID');
            }
            case 'DrugPrograms': {
                return this.formatDrugProgramEntry(entry);
            }
            default: {
                // Legacy format - check for name property
                if (entry.name) return entry.name;
                // Fall back to JSON string for unrecognized format
                return JSON.stringify(entry);
            }
        }
    }

    private formatProgramEntry(entry: any, idProperties: string[], lookupMap: Map<number, string>, idPrefix: string): string {
        const id = this.getIdProperty(entry, ...idProperties);
        if (id) {
            return lookupMap.get(id) || `${ idPrefix } ${ id }`;
        }
        // Legacy format fallback
        return entry.name || JSON.stringify(entry);
    }

    private formatDrugProgramEntry(entry: any): string {
        // Build a readable display for drug program entries
        const parts: string[] = [];

        if (entry.drugName) {
            parts.push(`Drug: ${ entry.drugName }`);
        } else if (entry.DrugName) {
            parts.push(`Drug: ${ entry.DrugName }`);
        }

        if (entry.drugProductId || entry.DrugProductId) {
            parts.push(`Product ID: ${ entry.drugProductId || entry.DrugProductId }`);
        }

        if (entry.packageId || entry.PackageId) {
            parts.push(`Package ID: ${ entry.packageId || entry.PackageId }`);
        }

        if (entry.ndc || entry.NDC) {
            parts.push(`NDC: ${ entry.ndc || entry.NDC }`);
        }

        if (entry.strength || entry.Strength) {
            parts.push(`Strength: ${ entry.strength || entry.Strength }`);
        }

        if (entry.dosageForm || entry.DosageForm) {
            parts.push(`Form: ${ entry.dosageForm || entry.DosageForm }`);
        }

        // Return a formatted string or fall back to JSON
        return parts.length > 0 ? parts.join(', ') : JSON.stringify(entry);
    }

    private async loadLookupData(url: string, targetMap: Map<number, string>, dataType: string): Promise<void> {
        try {
            const response = await axios.get(url);
            const items = response.data as Array<{ id: number; name: string }>;
            targetMap.clear();
            items.forEach(item => {
                targetMap.set(item.id, item.name);
            });
        } catch (error) {
            console.warn(`Failed to load ${ dataType }:`, error);
        }
    }

    async loadFunctionalityFlags(): Promise<void> {
        return this.loadLookupData('/Program/functionality-flags', this.functionalityFlags, 'functionality flags');
    }

    async loadTeams(): Promise<void> {
        return this.loadLookupData('/User/Teams', this.teams, 'teams');
    }

    // Refactored entity loading methods
    async loadPrescriberName(prescriberId: number): Promise<string> {
        return this.loadEntity('prescriber',
            prescriberId,
            `/Prescriber/${ prescriberId }`,
            (data) => `${ data.firstName } ${ data.lastName }`.trim() || data.name || `Prescriber ${ prescriberId }`,
            `Prescriber ${ prescriberId }`);
    }

    // Entity display methods with optimized reactivity
    getPrescriberDisplay(prescriberId: number): string {
        const cached = this.reactiveEntityCache['prescriber']?.[prescriberId];
        if (cached) return cached;

        this.loadPrescriberName(prescriberId);
        return "Loading...";
    }

    async loadPatientName(patientId: number): Promise<string> {
        return this.loadEntity('patient',
            patientId,
            `/Patient/${ patientId }`,
            (data) => `${ data.firstName } ${ data.lastName }`.trim() || data.name || `Patient ${ patientId }`,
            `Patient ${ patientId }`);
    }

    getPatientDisplay(patientId: number): string {
        const cached = this.reactiveEntityCache['patient']?.[patientId];
        if (cached) return cached;

        this.loadPatientName(patientId);
        return "Loading...";
    }

    async loadStoreName(storeId: number): Promise<string> {
        return this.loadEntity('store',
            storeId,
            `/Store/${ storeId }`,
            (data) => data.name || `Store ${ storeId }`,
            `Store ${ storeId }`);
    }

    getStoreDisplay(storeId: number): string {
        const cached = this.reactiveEntityCache['store']?.[storeId];
        if (cached) return cached;

        this.loadStoreName(storeId);
        return "Loading...";
    }

    async loadProgramName(programId: number): Promise<string> {
        return this.loadEntity('program',
            programId,
            `/Program/${ programId }`,
            (data) => data.name || `Program ${ programId }`,
            `Program ${ programId }`);
    }

    getProgramDisplay(programId: number): string {
        const cached = this.reactiveEntityCache['program']?.[programId];
        if (cached) return cached;

        this.loadProgramName(programId);
        return "Loading...";
    }

    async loadVendorName(vendorId: number): Promise<string> {
        return this.loadEntity('vendor',
            vendorId,
            `/Vendor/${ vendorId }`,
            (data) => data.name || `Vendor ${ vendorId }`,
            `Vendor ${ vendorId }`);
    }

    getVendorDisplay(vendorId: number): string {
        const cached = this.reactiveEntityCache['vendor']?.[vendorId];
        if (cached) return cached;

        this.loadVendorName(vendorId);
        return "Loading...";
    }

    async loadPbmName(pbmId: number): Promise<string> {
        return this.loadEntity('pbm',
            pbmId,
            `/PharmacyBenefitManager/${ pbmId }`,
            (data) => data.name || `PBM ${ pbmId }`,
            `PBM ${ pbmId }`);
    }

    getPbmDisplay(pbmId: number): string {
        const cached = this.reactiveEntityCache['pbm']?.[pbmId];
        if (cached) return cached;

        this.loadPbmName(pbmId);
        return "Loading...";
    }

    async loadDrugPackageDetails(packageId: number): Promise<DrugPackageDetails> {
        return this.loadEntity('drugPackage', packageId, `/Drug/${ packageId }`, (data) => ({
            name: data.overrideName || data.productNameShort || data.productNameLong || data.name || `Package ${ packageId }`,
            source: data.source !== undefined ? data.source : DrugSource.GsddFederal,
        }), { name: `Package ${ packageId }`, source: DrugSource.GsddFederal });
    }

    getDrugPackageDisplay(packageId: number): string {
        const cached = this.reactiveEntityCache['drugPackage']?.[packageId];
        if (cached) return cached.name;

        this.loadDrugPackageDetails(packageId);
        return "Loading...";
    }

    getDrugSourceDisplay(sourceId: number): string {
        const sourceName = DrugSource[sourceId];
        if (sourceName) return `${ sourceId } (${ sourceName })`;
        return sourceId?.toString();
    }

    getOriginCodeDisplay(originId: number): string {
        const originName = OriginCode[originId];
        if (originName) return `${ originId } (${ originName })`;
        return originId?.toString();
    }

    getDAWCodeDisplay(dawId: number): string {
        const dawName = DAWCode[dawId];
        if (dawName) return `${ dawId } (${ dawName })`;
        return dawId?.toString();
    }

    getDeliveryCodeDisplay(deliveryId: number): string {
        const deliveryName = DeliveryCode[deliveryId];
        if (deliveryName) return `${ deliveryId } (${ deliveryName })`;
        return deliveryId?.toString();
    }

    getLanguageDisplay(languageId: number): string {
        const languageName = Language[languageId];
        if (languageName) return `${ languageId } (${ languageName })`;
        return languageId?.toString();
    }

    getProviderTypeDisplay(providerTypeId: number): string {
        const providerTypeName = Plan.ProviderTypeToString(providerTypeId);
        if (providerTypeName && providerTypeName !== '') {
            return `${ providerTypeId } (${ providerTypeName })`;
        }
        return providerTypeId?.toString();
    }

    formatCurrency(value: number | string): string {
        if (value === null || value === undefined) {
            return '';
        }

        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        if (isNaN(numValue)) {
            return value.toString();
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(numValue);
    }

    // Generic cache methods
    private initializeEntityCache(entityType: string): void {
        if (!this.entityCache[entityType]) {
            this.$set(this.entityCache, entityType, {});
        }
        if (!this.loadingEntities[entityType]) {
            this.$set(this.loadingEntities, entityType, new Set());
        }
    }

    private getCachedEntity<T>(entityType: string, id: number): T | null {
        this.initializeEntityCache(entityType);
        return this.entityCache[entityType][id] || null;
    }

    private setCachedEntity<T>(entityType: string, id: number, value: T): void {
        this.initializeEntityCache(entityType);
        this.$set(this.entityCache[entityType], id, value);
        // More targeted reactivity - only increment when actual data changes
        this.cacheUpdateTrigger++;
    }

    // Computed property for reactive cache access
    get reactiveEntityCache() {
        // noinspection BadExpressionStatementJS
        this.cacheUpdateTrigger; // Ensure reactivity
        return this.entityCache;
    }

    private setEntityLoading(entityType: string, id: number, loading: boolean): void {
        this.initializeEntityCache(entityType);
        if (loading) {
            this.loadingEntities[entityType].add(id);
        } else {
            this.loadingEntities[entityType].delete(id);
        }

        // Clean up empty Sets to prevent memory leaks
        if (this.loadingEntities[entityType].size === 0) {
            delete this.loadingEntities[entityType];
        }
    }

    private cleanupCache(): void {
        // Clean up cache if it gets too large
        Object.keys(this.entityCache).forEach(entityType => {
            const cache = this.entityCache[entityType];
            const keys = Object.keys(cache);

            if (keys.length > this.maxCacheSize) {
                // Remove oldest entries (simple LRU approximation)
                const keysToRemove = keys.slice(0, keys.length - this.maxCacheSize);
                keysToRemove.forEach(key => {
                    delete cache[Number(key)];
                });
            }
        });

        // Clean up empty loading Sets
        Object.keys(this.loadingEntities).forEach(entityType => {
            if (this.loadingEntities[entityType].size === 0) {
                delete this.loadingEntities[entityType];
            }
        });
    }

    private async loadEntity<T>(entityType: string, id: number, apiPath: string, extractValue: (response: any) => T, fallbackValue: T): Promise<T> {
        const cached = this.getCachedEntity<T>(entityType, id);
        if (cached) return cached;

        // Request deduplication
        const requestKey = `${ entityType }-${ id }`;
        const existingRequest = this.pendingRequests.get(requestKey);
        if (existingRequest) {
            try {
                return await existingRequest;
            } catch {
                return fallbackValue;
            }
        }

        // Create new request
        const requestPromise = this.performEntityRequest<T>(entityType, id, apiPath, extractValue, fallbackValue);
        this.pendingRequests.set(requestKey, requestPromise);

        try {
            const result = await requestPromise;
            return result;
        } finally {
            this.pendingRequests.delete(requestKey);
        }
    }

    private async performEntityRequest<T>(entityType: string, id: number, apiPath: string, extractValue: (response: any) => T, fallbackValue: T): Promise<T> {
        this.setEntityLoading(entityType, id, true);

        try {
            const response = await axios.get(apiPath, {
                timeout: 10000, // 10 second timeout
            });
            const value = extractValue(response.data);
            this.setCachedEntity(entityType, id, value);
            return value;
        } catch (error) {
            console.warn(`Failed to load ${ entityType } ${ id }:`, error);

            // More specific error handling
            if (axios.isAxiosError(error)) {
                if (error.code === 'ECONNABORTED') {
                    console.warn(`Request timeout for ${ entityType } ${ id }`);
                } else if (error.response?.status === 404) {
                    console.warn(`${ entityType } ${ id } not found`);
                } else if (error.response?.status !== undefined && error.response.status >= 500) {
                    console.warn(`Server error loading ${ entityType } ${ id }`);
                }
            }

            this.setCachedEntity(entityType, id, fallbackValue);
            return fallbackValue;
        } finally {
            this.setEntityLoading(entityType, id, false);
        }
    }

    async loadOnHoldSubStatuses(): Promise<void> {
        if (this.loadingOnHoldSubStatuses || Object.keys(this.onHoldSubStatusCache).length > 0) {
            return;
        }

        this.loadingOnHoldSubStatuses = true;

        try {
            const response = await axios.get('/Prescription/OnHoldSubStatus');
            const onHoldSubStatuses = response.data as SubStatus[];

            // Cache all OnHold substatuses
            onHoldSubStatuses.forEach(subStatus => {
                if (subStatus.id !== null) {
                    this.$set(this.onHoldSubStatusCache, subStatus.id, subStatus.name);
                }
            });
        } catch (error) {
            console.warn('Failed to load OnHold substatuses:', error);
        } finally {
            this.loadingOnHoldSubStatuses = false;
        }
    }

    async loadDiscontinuedSubStatuses(): Promise<void> {
        if (this.loadingDiscontinuedSubStatuses || Object.keys(this.discontinuedSubStatusCache).length > 0) {
            return;
        }

        this.loadingDiscontinuedSubStatuses = true;

        try {
            const response = await axios.get('/Prescription/DiscontinuedSubStatus');
            const discontinuedSubStatuses = response.data as SubStatus[];

            // Cache all Discontinued substatuses
            discontinuedSubStatuses.forEach(subStatus => {
                if (subStatus.id !== null) {
                    this.$set(this.discontinuedSubStatusCache, subStatus.id, subStatus.name);
                }
            });
        } catch (error) {
            console.warn('Failed to load Discontinued substatuses:', error);
        } finally {
            this.loadingDiscontinuedSubStatuses = false;
        }
    }

    getSubStatusDisplay(subStatusId: number, statusCode: number): string {
        if (statusCode === null || statusCode === undefined) return `SubStatus ${ subStatusId }`;

        if (isNaN(subStatusId)) return '';

        // Determine which cache to use based on status
        const isOnHold = statusCode === RefillStatusCode.OnHold;
        const isDiscontinued = statusCode === RefillStatusCode.Discontinued;

        if (isOnHold) {
            // Load OnHold substatuses if not already loaded
            if (Object.keys(this.onHoldSubStatusCache).length === 0) {
                this.loadOnHoldSubStatuses();
                return "Loading...";
            }

            return this.onHoldSubStatusCache[subStatusId] || `OnHold SubStatus ${ subStatusId }`;
        } else if (isDiscontinued) {
            // Load Discontinued substatuses if not already loaded
            if (Object.keys(this.discontinuedSubStatusCache).length === 0) {
                this.loadDiscontinuedSubStatuses();
                return "Loading...";
            }

            return this.discontinuedSubStatusCache[subStatusId] || `Discontinued SubStatus ${ subStatusId }`;
        }

        // For other statuses, return the raw ID since substatus might not be relevant
        return `SubStatus ${ subStatusId }`;
    }

    getStatusDisplay(statusCode: number): string {
        if (isNaN(statusCode)) return '';

        // Determine which enum to use based on the entity type
        const searchTypeKey = typeof this.activeSearchType === 'string'
            ? this.activeSearchType
            : this.activeSearchType.value || this.activeSearchType.label;

        let statusName: string | undefined;
        if (searchTypeKey === 'Order') {
            statusName = OrderStatus[statusCode];
        } else {
            // Default to RefillStatusCode for Prescription and other types
            statusName = RefillStatusCode[statusCode];
        }

        if (statusName) return `${ statusCode } (${ statusName })`;
        return statusCode.toString();
    }

    // Unified field display method
    getFormattedValue(fieldKey: string, value: any, historyItem?: any): string {
        if (value === null || value === undefined) {
            return '';
        }

        const lowerKey = fieldKey.toLowerCase();

        // Entity ID fields
        if (this.isEntityIdField(fieldKey)) {
            if (lowerKey.includes('patient')) {
                return this.getPatientDisplay(value);
            } else if (lowerKey.includes('prescriber')) {
                return this.getPrescriberDisplay(value);
            } else if (lowerKey.includes('store')) {
                return this.getStoreDisplay(value);
            } else if (lowerKey.includes('packageid') || lowerKey === 'packageid') {
                return this.getDrugPackageDisplay(value);
            } else if (lowerKey.includes('program')) {
                return this.getProgramDisplay(value);
            } else if (lowerKey.includes('vendor')) {
                return this.getVendorDisplay(value);
            } else if (lowerKey === 'pbmid' || lowerKey.includes('pbmid')) {
                return this.getPbmDisplay(value);
            }
        }

        // Enum fields
        if (lowerKey === 'source' || lowerKey.includes('drugsource')) {
            return this.getDrugSourceDisplay(value);
        } else if (lowerKey === 'providertype' || lowerKey.includes('providertype')) {
            return this.getProviderTypeDisplay(value);
        } else if (this.isOriginCodeField(fieldKey)) {
            return this.getOriginCodeDisplay(value);
        } else if (this.isDAWCodeField(fieldKey)) {
            return this.getDAWCodeDisplay(value);
        } else if (this.isDeliveryCodeField(fieldKey)) {
            return this.getDeliveryCodeDisplay(value);
        } else if (this.isLanguageField(fieldKey)) {
            return this.getLanguageDisplay(value);
        }

        // Status fields
        if (lowerKey === 'substatus' || lowerKey.includes('substatus')) {
            let statusCode = null;
            if (historyItem) statusCode = historyItem.status || historyItem.Status;
            const subStatusDisplay = this.getSubStatusDisplay(value, statusCode);
            if (subStatusDisplay.length > 0) return `${ value } (${ subStatusDisplay })`;
            return '';
        } else if (lowerKey === 'status' || lowerKey.includes('status')) {
            return this.getStatusDisplay(value);
        }

        // Currency fields
        if (this.isCurrencyField(fieldKey)) {
            return this.formatCurrency(value);
        }

        // Bitwise fields
        const bitwiseExplanation = this.getBitwiseExplanation(fieldKey, value);
        if (bitwiseExplanation) {
            return `${ value } (${ bitwiseExplanation.join(', ') })`;
        }

        // Date fields
        if (this.isDateField(fieldKey)) {
            const date = moment.utc(value).local();
            if (!date.isValid()) {
                return value.toString();
            }
            // Use date-only format for birth dates and HIPAA docs received, full date+time for others
            if (lowerKey.includes('birth') || lowerKey.includes('hipaadocsreceived')) {
                return date.format("L");
            }
            return date.format("L LT");
        }

        return value.toString();
    }

    // Legacy method for backward compatibility
    getEntityDisplay(fieldKey: string, entityId: number, historyItem?: any): string {
        return this.getFormattedValue(fieldKey, entityId, historyItem);
    }

    // Entity link generation methods
    getEntityLink(fieldKey: string, entityId: number): string | null {
        const lowerKey = fieldKey.toLowerCase();

        // Special handling for PackageId - need to check drug source
        if (lowerKey.includes('packageid') || lowerKey === 'packageid') {
            // Get the drug source from cache
            const drugDetails = this.getCachedEntity<{ name: string; source: DrugSource }>('drugPackage', entityId);
            if (drugDetails) {
                switch (drugDetails.source) {
                    case DrugSource.NonGsddProduct:
                        return `/maintenance/non-gsdd-drug/${ entityId }`;
                    case DrugSource.CompoundProduct:
                        return `/maintenance/compound-drug/${ entityId }`;
                    case DrugSource.GsddFederal:
                    default:
                        return `/maintenance/drug/${ entityId }`;
                }
            }
            // Default to GSDD drug if not cached yet
            // Trigger loading so we can update the link later
            this.loadDrugPackageDetails(entityId);
            return `/maintenance/drug/${ entityId }`;
        }

        const entityMappings: Record<string, string> = {
            'patientid': '/processing/patient-dashboard/',
            'prescriberid': '/maintenance/doctor/',
            'storeid': '/maintenance/store/',
            'orderid': '/processing/order/',
            'programid': '/maintenance/program/',
            'vendorid': '/maintenance/vendor/',
            'taskid': '/processing/tasks/',
            'caseid': '/processing/case/',
            'planid': '/maintenance/plan/',
            'pbmid': '/maintenance/pharmacyBenefitManager/',
        };

        const basePath = entityMappings[lowerKey];
        return basePath ? `${ basePath }${ entityId }` : null;
    }

    isEntityIdField(fieldKey: string): boolean {
        const lowerKey = fieldKey.toLowerCase();
        // Special case for PackageId/PackageID
        if (lowerKey === 'packageid' || lowerKey.includes('packageid')) {
            return true;
        }
        return (lowerKey.endsWith('id') || lowerKey === 'id') && this.getEntityLink(fieldKey, 0) !== null;
    }

    // Consolidated field type detection
    private readonly fieldTypePatterns = {
        status: [ 'status', 'substatus' ],
        drugSource: [ 'source', 'drugsource' ],
        originCode: [ 'origincode' ],
        dawCode: [ 'dawcode' ],
        deliveryCode: [ 'deliverycode' ],
        language: [ 'primarylanguage', 'language' ],
        currency: [
            'totalprice',
            'cost',
            'patientpay',
            'tax',
            'price',
            'amount',
            'copay',
            'deductible',
            'coinsurance',
            'fee',
            'charge',
            'payment',
            'balance',
            'total',
            'subtotal',
            'discount',
            'rebate',
        ],
        packageId: [ 'packageid' ],
        date: [
            'date',
            'dateofbirth',
            'filldate',
            'writtendate',
            'expirationdate',
            'expdate',
            'originalrxdate',
            'drugsfilleddate',
            'drugexpirationdate',
            'licenseexpirationdate',
            'textmessageoptoutdate',
            'approvalexpirationdate',
            'promiseddate',
            'hipaadocsreceived',
        ],
    };

    private isFieldType(fieldKey: string, type: keyof typeof this.fieldTypePatterns): boolean {
        const lowerKey = fieldKey.toLowerCase();
        const patterns = this.fieldTypePatterns[type];
        return patterns.some(pattern => lowerKey === pattern || lowerKey.includes(pattern));
    }

    isStatusField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'status');
    }

    isDrugSourceField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'drugSource');
    }

    isOriginCodeField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'originCode');
    }

    isDAWCodeField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'dawCode');
    }

    isDeliveryCodeField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'deliveryCode');
    }

    isLanguageField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'language');
    }

    isCurrencyField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'currency');
    }

    isDateField(fieldKey: string): boolean {
        return this.isFieldType(fieldKey, 'date');
    }

    // Bitwise value explanation methods
    private readonly bitwiseMappings: Record<string, any> = {
        'flags': BillingFlags,
        'Flags': BillingFlags,  // Handle both lowercase and uppercase
        'capabilities': StoreCapabilities,
        'Capabilities': StoreCapabilities  // Handle both lowercase and uppercase
    };

    getBitwiseExplanation(fieldKey: string, value: number): string[] | null {
        // Check both exact key and lowercase version
        const enumMapping = this.bitwiseMappings[fieldKey] || this.bitwiseMappings[fieldKey.toLowerCase()];
        if (!enumMapping || typeof (value as any) !== 'number' || value === 0) return null;

        const activeFlags: string[] = [];
        for (const [ key, flagValue ] of Object.entries(enumMapping)) {
            if (typeof flagValue === 'number' && flagValue > 0 && (value & flagValue) === flagValue) {
                activeFlags.push(this.formatFlagName(key));
            }
        }

        return activeFlags.length > 0 ? activeFlags : null;
    }

    private formatFlagName(flagName: string): string {
        // Convert PascalCase to readable format
        return flagName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^\s/, '')
            .replace(/([a-z])([A-Z])/g, '$1 $2');
    }

    getEnhancedTooltip(diffItem: any): string {
        const baseTooltip = diffItem.oldValue || diffItem.newValue ? `${ diffItem.oldValue } → ${ diffItem.newValue }` : '';

        if (this.isEntityIdField(diffItem.key)) {
            const entityType = diffItem.key.replace(/Id$|ID$/i, '').toLowerCase();
            return `${ baseTooltip }\nClick to navigate to ${ entityType } details`;
        }

        const bitwiseOld = this.getBitwiseExplanation(diffItem.key, diffItem.oldValue);
        const bitwiseNew = this.getBitwiseExplanation(diffItem.key, diffItem.newValue);

        if (bitwiseOld || bitwiseNew) {
            let tooltip = baseTooltip;
            if (bitwiseOld) tooltip += `\nOld flags: ${ bitwiseOld.join(', ') }`;
            if (bitwiseNew) tooltip += `\nNew flags: ${ bitwiseNew.join(', ') }`;
            return tooltip;
        }

        return baseTooltip;
    }
}

</script>

<!--suppress CssUnusedSymbol -->
<style scoped>
#property-names {
    text-align: center;
}

#old-values {
    /*noinspection CssUnresolvedCustomProperty*/
    color: var(--color-danger);
}

#new-values {
    /*noinspection CssUnresolvedCustomProperty*/
    color: var(--color-save);
}

#title-row {
    font-weight: bold;
    text-decoration: underline;
    text-align: center;
}

.cell {
    border: thin solid #808080;
}

.vdp-datepicker {
    background: #ffffff;
    color: #000000;
}

::v-deep(.toggle-col) {
    width: 40px;
}

::v-deep(.details-card) {
    box-shadow: none;
}

::v-deep(.details-card .card-body) {
    padding: 0;
}

::v-deep(.details-card .card-body table) {
    margin-bottom: 0;
    font-size: .9em;
}

::v-deep(table td) {
    width: 33%;
}

::v-deep .b-table-details > td {
    padding-left: 30px;
    padding-right: 30px;
}

::v-deep(.badge-secondary) {
    color: rgb(58, 58, 58);
}

.field-filters {
    gap: 5px;
}

.field-filters .badge {
    cursor: pointer;
}

.entity-link-container {
    display: flex;
    align-items: center;

    a {
        display: inline-flex;
        align-items: center;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
}

.bitwise-explanation {
    .flag-list {
        margin-top: 5px;

        .badge {
            font-size: 0.75em;
        }
    }
}

.bitwise-value {
    .flag-list {
        margin-top: 5px;

        .badge {
            font-size: 0.75em;
        }
    }
}

.fa-external-link-alt, .fas.fa-external-link-alt {
    font-size: 0.8em;
    opacity: 0.7;
}
</style>
